/**
 * FinanceApprovalListPage
 * 
 * Lista de requisições prontas para aprovação financeira.
 * Exibe KPIs financeiros, cards de requisições com badges de categoria (OPEX/CAPEX) e prioridade.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, DollarSign,
  User, Clock, ChevronRight, LayoutList,
  TrendingUp, AlertCircle, TrendingDown,
  Building2, Package, Calendar
} from 'lucide-react';
import { financeService } from '../../../services/purchasesApi';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components';
import './FinanceApprovalListPage.css';

const FinanceApprovalListPage = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all'); // all, opex, capex
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar lista de requisições para aprovação financeira
  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;
    
    const loadFinanceList = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Timeout de segurança (30 segundos)
        timeoutId = setTimeout(() => {
          if (!cancelled) {
            setLoading(false);
            setError('Tempo de carregamento excedido. Tente novamente.');
            setRequisitions([]);
          }
        }, 30000);
        
        const response = await financeService.getList();
        
        clearTimeout(timeoutId);
        
        if (cancelled) return;
        
        if (response && response.success) {
          setRequisitions(response.data || []);
        } else {
          const errorMessage = response?.message || 'Erro ao carregar lista de aprovação financeira';
          setError(errorMessage);
          setRequisitions([]);
          if (showToast) {
            showToast(errorMessage, 'error');
          }
        }
      } catch (err) {
        if (timeoutId) clearTimeout(timeoutId);
        if (cancelled) return;
        
        const errorMessage = err.response?.data?.message || err.message || 'Erro ao carregar lista de aprovação financeira';
        setError(errorMessage);
        setRequisitions([]);
        if (showToast) {
          showToast(errorMessage, 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadFinanceList();
    
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Removido showToast da dependência para evitar loops

  // Calcular KPIs financeiros
  const kpis = useMemo(() => {
    if (!requisitions || requisitions.length === 0) {
      return {
        totalPending: 0,
        totalValue: 0,
        averageValue: 0,
        opexCount: 0,
        capexCount: 0,
        opexValue: 0,
        capexValue: 0
      };
    }

    const getReqValue = (req) => parseFloat(req.total_value ?? req.totalValue ?? 0) || 0;

    const totalValue = requisitions.reduce((sum, req) => sum + getReqValue(req), 0);

    const opexReqs = requisitions.filter(req => {
      const category = (req.financial_category || req.category || 'opex').toLowerCase();
      return category === 'opex';
    });

    const capexReqs = requisitions.filter(req => {
      const category = (req.financial_category || req.category || 'opex').toLowerCase();
      return category === 'capex';
    });

    const opexValue = opexReqs.reduce((sum, req) => sum + getReqValue(req), 0);

    const capexValue = capexReqs.reduce((sum, req) => sum + getReqValue(req), 0);

    return {
      totalPending: requisitions.length,
      totalValue,
      averageValue: requisitions.length > 0 ? totalValue / requisitions.length : 0,
      opexCount: opexReqs.length,
      capexCount: capexReqs.length,
      opexValue,
      capexValue
    };
  }, [requisitions]);

  // Filtrar requisições
  const filteredRequisitions = useMemo(() => {
    if (!requisitions) return [];
    
    return requisitions.filter(req => {
      const matchesSearch = !searchTerm || 
        req.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.id?.toString().includes(searchTerm) ||
        req.requester_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const category = req.financial_category || req.category || 'opex';
      const matchesCategory = filterCategory === 'all' || 
        category.toLowerCase() === filterCategory.toLowerCase();
      
      return matchesSearch && matchesCategory;
    });
  }, [requisitions, searchTerm, filterCategory]);

  // Obter configuração de prioridade
  const getPriorityConfig = (priority) => {
    const configs = {
      critical: { label: 'Crítico', className: 'priority-critical', icon: AlertCircle },
      urgent: { label: 'Urgente', className: 'priority-urgent', icon: TrendingUp },
      normal: { label: 'Normal', className: 'priority-normal', icon: Clock },
      low: { label: 'Baixa', className: 'priority-low', icon: TrendingDown }
    };
    return configs[priority] || configs.normal;
  };

  // Obter configuração de categoria
  const getCategoryConfig = (category) => {
    const cat = category || 'opex';
    const configs = {
      opex: { label: 'OPEX', className: 'category-opex', icon: Package },
      capex: { label: 'CAPEX', className: 'category-capex', icon: Building2 }
    };
    return configs[cat.toLowerCase()] || configs.opex;
  };

  // Formatar data
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  // Formatar valor
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(num);
  };

  return (
    <div className="finance-approval-list-page">
      {/* Header */}
      <div className="finance-list-header">
        <div>
          <h1 className="finance-list-title">
            Aprovação <span className="finance-list-title-highlight">Financeira</span>
          </h1>
          <p className="finance-list-subtitle">
            Análise de Viabilidade e Impacto no Fluxo de Caixa
          </p>
        </div>
        <button 
          onClick={() => navigate('/erp/purchases')} 
          className="finance-list-back-button"
        >
          <ArrowLeft size={16} />
          Painel de Compras
        </button>
      </div>

      {/* KPI Cards */}
      <div className="finance-kpis-grid">
        <div className="finance-kpi-card">
          <div className="finance-kpi-icon finance-kpi-icon-primary">
            <DollarSign size={20} />
          </div>
          <div className="finance-kpi-content">
            <p className="finance-kpi-label">Total Pendente</p>
            <p className="finance-kpi-value">{kpis.totalPending}</p>
            <p className="finance-kpi-sublabel">Requisições</p>
          </div>
        </div>

        <div className="finance-kpi-card">
          <div className="finance-kpi-icon finance-kpi-icon-success">
            <TrendingUp size={20} />
          </div>
          <div className="finance-kpi-content">
            <p className="finance-kpi-label">Valor Total</p>
            <p className="finance-kpi-value">{formatCurrency(kpis.totalValue)}</p>
            <p className="finance-kpi-sublabel">Em análise</p>
          </div>
        </div>

        <div className="finance-kpi-card">
          <div className="finance-kpi-icon finance-kpi-icon-warning">
            <Package size={20} />
          </div>
          <div className="finance-kpi-content">
            <p className="finance-kpi-label">OPEX</p>
            <p className="finance-kpi-value">{formatCurrency(kpis.opexValue)}</p>
            <p className="finance-kpi-sublabel">{kpis.opexCount} requisições</p>
          </div>
        </div>

        <div className="finance-kpi-card">
          <div className="finance-kpi-icon finance-kpi-icon-info">
            <Building2 size={20} />
          </div>
          <div className="finance-kpi-content">
            <p className="finance-kpi-label">CAPEX</p>
            <p className="finance-kpi-value">{formatCurrency(kpis.capexValue)}</p>
            <p className="finance-kpi-sublabel">{kpis.capexCount} requisições</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="finance-list-toolbar">
        <div className="finance-list-search">
          <Search className="finance-list-search-icon" size={18} />
          <input
            type="text"
            placeholder="Buscar por ID, título, solicitante ou fornecedor..."
            className="finance-list-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="finance-list-filters">
          <select
            className="finance-list-filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">Todas as Categorias</option>
            <option value="opex">OPEX</option>
            <option value="capex">CAPEX</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="finance-list-loading">
          <div className="finance-list-loading-spinner"></div>
          <p>Carregando requisições para aprovação financeira...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="finance-list-error">
          <AlertCircle size={24} />
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="finance-list-retry-button"
          >
            Tentar Novamente
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredRequisitions.length === 0 && (
        <div className="finance-list-empty">
          <LayoutList size={48} />
          <p>Nenhuma requisição encontrada</p>
          <span>
            {searchTerm || filterCategory !== 'all' 
              ? 'Tente ajustar os filtros de busca'
              : 'Não há requisições aguardando aprovação financeira'}
          </span>
        </div>
      )}

      {/* Grid de Cards */}
      {!loading && !error && filteredRequisitions.length > 0 && (
        <div className="finance-list-grid">
          {filteredRequisitions.map((req) => {
            const priorityConfig = getPriorityConfig(req.priority || 'normal');
            const categoryConfig = getCategoryConfig(req.category || req.financial_category);
            const PriorityIcon = priorityConfig.icon;
            const CategoryIcon = categoryConfig.icon;
            
            return (
              <button
                key={req.id}
                onClick={() => navigate(`/erp/purchases/finance/approve/${req.id}`)}
                className="finance-list-card"
              >
                {/* Background Glow based on priority */}
                <div className={`finance-card-glow ${priorityConfig.className}`} />

                <div className="finance-card-header">
                  <div className="finance-card-badges">
                    <span className={`finance-category-badge ${categoryConfig.className}`}>
                      {CategoryIcon && <CategoryIcon size={10} />}
                      {categoryConfig.label}
                    </span>
                    <span className={`finance-priority-badge ${priorityConfig.className}`}>
                      <PriorityIcon size={10} />
                      {priorityConfig.label}
                    </span>
                  </div>
                  <span className="finance-card-id">#{req.id}</span>
                </div>
                
                <h3 className="finance-card-title">{req.title || 'Sem título'}</h3>
                
                <div className="finance-card-meta">
                  <div className="finance-card-meta-item">
                    <User size={12} />
                    <span>{req.requester_name || 'N/A'}</span>
                  </div>
                  <div className="finance-card-meta-item">
                    <Calendar size={12} />
                    <span>{formatDate(req.created_at)}</span>
                  </div>
                </div>

                <div className="finance-card-footer">
                  <div className="finance-card-value">
                    <p className="finance-card-value-label">Valor Total</p>
                    <p className="finance-card-value-amount">
                      {formatCurrency(req.total_value || req.totalValue || 0)}
                    </p>
                  </div>
                  <div className="finance-card-arrow">
                    <ChevronRight size={18} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Toast */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  );
};

export default FinanceApprovalListPage;
